import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, LinearProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add, Receipt } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '../../../shared/api';
import type { Class, FeePayment, Student, StudentFee } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { formatINR, formatDate } from '../../../shared/utils';

export default function FeesPage() {
  const user = useAuthStore((s) => s.user);
  if (user?.role === 'STUDENT') return <StudentFeesView />;
  return <AdminFeesView />;
}

function StudentFeesView() {
  const dashQ = useQuery({ queryKey: ['student-dashboard'], queryFn: async () => (await api.get('/dashboard/student')).data });
  const studentId = dashQ.data?.student?.id;

  const { data } = useQuery({
    queryKey: ['student-fees', studentId],
    queryFn: async () => (await api.get(`/students/${studentId}/fees`)).data,
    enabled: !!studentId,
  });

  if (!data) return <Typography>Loading…</Typography>;
  const fee: StudentFee | undefined = data.fee;
  const payments: FeePayment[] = data.payments ?? [];
  const pct = fee?.totalDue ? Math.round(fee.totalPaid / fee.totalDue * 100) : 0;

  return (
    <Box>
      <PageHeader title="My Fees" subtitle="Your fee summary and payment history for the current session." />
      {fee ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card><CardContent>
                <Typography variant="overline" color="text.secondary">TOTAL DUE</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{formatINR(fee.totalDue)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card><CardContent>
                <Typography variant="overline" color="text.secondary">PAID</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>{formatINR(fee.totalPaid)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card><CardContent>
                <Typography variant="overline" color="text.secondary">BALANCE</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: fee.balance > 0 ? 'error.main' : 'success.main' }}>
                  {formatINR(fee.balance)}
                </Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12}>
              <Card><CardContent>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payment progress</Typography>
                  <Typography variant="caption">{pct}% paid</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={pct} sx={{ height: 12, borderRadius: 1 }} />
              </CardContent></Card>
            </Grid>
            <Grid item xs={12}>
              <Card><CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Payment History</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Receipt #</TableCell><TableCell>Mode</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                    <TableBody>
                      {payments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{formatDate(p.paidOn)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{p.receiptNo}</TableCell>
                          <TableCell><Chip size="small" label={p.paymentMode} /></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{formatINR(p.amount)}</TableCell>
                        </TableRow>
                      ))}
                      {payments.length === 0 && <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>No payments yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent></Card>
            </Grid>
          </Grid>
        </>
      ) : (
        <Alert severity="info">No fee structure assigned for this session.</Alert>
      )}
    </Box>
  );
}

function AdminFeesView() {
  const [classFilter, setClassFilter] = useState<string>('');
  const [recordingFor, setRecordingFor] = useState<{ student: Student; fee?: StudentFee } | null>(null);

  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const summaryQ = useQuery({
    queryKey: ['fees-summary', classFilter],
    queryFn: async () => (await api.get<{ student: Student; class: Class; fee: StudentFee }[]>('/admin/fees/summary', { params: { ...(classFilter && { classId: classFilter }) } })).data,
  });

  const totals = useMemo(() => {
    const list = summaryQ.data ?? [];
    return list.reduce((acc, x) => ({
      due: acc.due + (x.fee?.totalDue ?? 0),
      paid: acc.paid + (x.fee?.totalPaid ?? 0),
      bal: acc.bal + (x.fee?.balance ?? 0),
    }), { due: 0, paid: 0, bal: 0 });
  }, [summaryQ.data]);

  return (
    <Box>
      <PageHeader title="Fees" subtitle="Class-wise fee collection and payments." />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}><Card><CardContent><Typography variant="overline">TOTAL DUE</Typography><Typography variant="h4" sx={{ fontWeight: 700 }}>{formatINR(totals.due)}</Typography></CardContent></Card></Grid>
        <Grid item xs={12} md={4}><Card><CardContent><Typography variant="overline">COLLECTED</Typography><Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>{formatINR(totals.paid)}</Typography></CardContent></Card></Grid>
        <Grid item xs={12} md={4}><Card><CardContent><Typography variant="overline">OUTSTANDING</Typography><Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>{formatINR(totals.bal)}</Typography></CardContent></Card></Grid>
      </Grid>

      <Card sx={{ p: 2, mb: 2 }}>
        <TextField select label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">All classes</MenuItem>
          {(classesQ.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}-{c.section}</MenuItem>)}
        </TextField>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Roll</TableCell><TableCell>Student</TableCell><TableCell>Class</TableCell>
            <TableCell align="right">Due</TableCell><TableCell align="right">Paid</TableCell>
            <TableCell align="right">Balance</TableCell><TableCell>Status</TableCell><TableCell align="right">Action</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {(summaryQ.data ?? []).map((row) => (
              <TableRow key={row.student.id} hover>
                <TableCell sx={{ fontWeight: 700 }}>{row.student.rollNumber}</TableCell>
                <TableCell>{row.student.firstName} {row.student.lastName}</TableCell>
                <TableCell>{row.class?.name}-{row.class?.section}</TableCell>
                <TableCell align="right">{formatINR(row.fee?.totalDue ?? 0)}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>{formatINR(row.fee?.totalPaid ?? 0)}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>{formatINR(row.fee?.balance ?? 0)}</TableCell>
                <TableCell>
                  {row.fee?.status === 'PAID' && <Chip size="small" color="success" label="Paid" />}
                  {row.fee?.status === 'PARTIAL' && <Chip size="small" color="warning" label="Partial" />}
                  {row.fee?.status === 'PENDING' && <Chip size="small" color="error" label="Pending" />}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<Add />} onClick={() => setRecordingFor({ student: row.student, fee: row.fee })} disabled={row.fee?.status === 'PAID'}>
                    Record Payment
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <RecordPaymentDialog state={recordingFor} onClose={() => setRecordingFor(null)} />
    </Box>
  );
}

function RecordPaymentDialog({ state, onClose }: { state: { student: Student; fee?: StudentFee } | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number>(0);
  const [paidOn, setPaidOn] = useState<string>(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState<FeePayment['paymentMode']>('CASH');
  const [remarks, setRemarks] = useState('');

  const save = useMutation({
    mutationFn: async () => {
      if (!state) return;
      return (await api.post(`/students/${state.student.id}/fees/payments`, {
        amount, paidOn, paymentMode: mode, remarks,
      })).data;
    },
    onSuccess: () => { qc.invalidateQueries(); onClose(); setAmount(0); setRemarks(''); },
  });

  return (
    <Dialog open={!!state} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Record Payment — {state?.student.firstName} {state?.student.lastName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info" icon={<Receipt />}>
            Outstanding balance: <b>{formatINR(state?.fee?.balance ?? 0)}</b>
          </Alert>
          <TextField label="Amount" type="number" value={amount || ''} onChange={(e) => setAmount(Number(e.target.value))} inputProps={{ min: 0 }} />
          <TextField label="Paid on" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField select label="Payment mode" value={mode} onChange={(e) => setMode(e.target.value as FeePayment['paymentMode'])}>
            {['CASH', 'CHEQUE', 'BANK_TRANSFER', 'CARD', 'UPI'].map((m) => <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <TextField label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          {save.isError && <Alert severity="error">{extractError(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending || amount <= 0}>
          {save.isPending ? 'Saving…' : 'Record payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
